import React, { useState } from 'react';
import { getApiUrl, fetchWithRetry } from '../services/api';
import SubscriptionWarningModal from './SubscriptionWarningModal';

interface PaywallProps {
  onSelectFreeTier: () => void;
  initialError?: string;
}

// The payment provider is now determined at build time from a CI environment variable.
// Vite replaces `process.env.VITE_PAYMENT_PROVIDER` with its value at build time.
// It defaults to 'paypal' if the variable is not set or is not 'stripe'.
const PAYMENT_PROVIDER = process.env.VITE_PAYMENT_PROVIDER === 'stripe' ? 'stripe' : 'paypal';


const Paywall: React.FC<PaywallProps> = ({ onSelectFreeTier, initialError }) => {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(initialError || null);
  const [warningPlan, setWarningPlan] = useState<'monthly' | 'yearly' | null>(null);

  // The runtime config fetching logic has been removed.

  const handleCheckout = (plan: 'monthly' | 'yearly') => {
    setWarningPlan(plan);
  };
  
  const proceedToCheckout = async (plan: 'monthly' | 'yearly') => {
    setWarningPlan(null); // Close modal
    setError(null);
    setIsLoading(plan);

    try {
        const origin = window.location.origin;

        // This single endpoint starts a checkout with the provider configured on the backend.
        const response = await fetchWithRetry(await getApiUrl('/create-checkout-session'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plan, origin }),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to create a checkout session.');
        }

        if (data.url) {
            // Redirect the user to the provider's hosted checkout page.
            window.location.href = data.url;
        } else {
            throw new Error('No checkout URL returned from the server.');
        }

    } catch (err: any) {
        console.error('Checkout error:', err);
        setError(err.message || 'An unknown error occurred. Please try again.');
        setIsLoading(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, plan: 'monthly' | 'yearly') => {
    if ((e.key === 'Enter' || e.key === ' ') && !isLoading) {
      e.preventDefault();
      handleCheckout(plan);
    }
  };

  const renderPaymentOptions = () => {
    const providerName = PAYMENT_PROVIDER === 'stripe' ? 'Stripe' : 'PayPal';
    return (
        <>
            <div className="space-y-4">
                {/* Monthly Plan */}
                <div
                onClick={() => !isLoading && handleCheckout('monthly')}
                onKeyDown={(e) => handleKeyDown(e, 'monthly')}
                role="button"
                tabIndex={isLoading ? -1 : 0}
                className={`p-5 border border-slate-200 dark:border-slate-700 rounded-lg text-left flex items-center transition-all duration-300 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-lg hover:border-teal-600 dark:hover:border-teal-500 hover:-translate-y-1'}`}
                aria-disabled={!!isLoading}
                >
                <div className="flex-grow">
                    <h3 className="font-semibold text-slate-800 dark:text-slate-100">Monthly Plan</h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">Billed every month.</p>
                </div>
                {isLoading === 'monthly' ? (
                    <svg className="animate-spin h-6 w-6 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                ) : (
                    <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">$1<span className="text-base font-normal text-slate-500 dark:text-slate-400">/mo</span></div>
                )}
                </div>
                {/* Yearly Plan */}
                <div
                onClick={() => !isLoading && handleCheckout('yearly')}
                onKeyDown={(e) => handleKeyDown(e, 'yearly')}
                role="button"
                tabIndex={isLoading ? -1 : 0}
                className={`relative p-5 border-2 rounded-lg text-left flex items-center transition-all duration-300 ${isLoading ? 'opacity-50 cursor-not-allowed border-slate-300 dark:border-slate-600' : 'cursor-pointer hover:shadow-lg hover:-translate-y-1 border-teal-500'}`}
                aria-disabled={!!isLoading}
                >
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full">BEST VALUE</div>
                <div className="flex-grow">
                    <h3 className="font-semibold text-slate-800 dark:text-slate-100">Yearly Plan</h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">Save 17% with annual billing.</p>
                </div>
                {isLoading === 'yearly' ? (
                    <svg className="animate-spin h-6 w-6 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                ) : (
                    <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">$10<span className="text-base font-normal text-slate-500 dark:text-slate-400">/yr</span></div>
                )}
                </div>
            </div>
            
            <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
                Payment processing is handled securely by {providerName}.
            </p>
        </>
    );
  };
  
  // No more loading state needed for config fetching.
  return (
    <>
      {warningPlan && (
        <SubscriptionWarningModal 
          onCancel={() => setWarningPlan(null)}
          onContinue={() => proceedToCheckout(warningPlan)}
        />
      )}
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
                <svg className="h-12 w-12 mx-auto text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mt-4">Upgrade to Pro</h2>
                <p className="text-slate-500 dark:text-slate-400 mt-2">Unlock unlimited features and remove ads.</p>
            </div>
            {error && (
                <div className="mb-6 p-3 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-900/40 dark:text-red-300" role="alert">
                   <span className="font-medium">Oops!</span> {error}
                </div>
            )}
            
            {renderPaymentOptions()}

            <div className="relative flex py-4 items-center">
                <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
                <span className="flex-shrink mx-4 text-slate-400 dark:text-slate-500 text-xs uppercase font-semibold">Or</span>
                <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
            </div>

            <div className="text-center">
                <button
                    onClick={onSelectFreeTier}
                    className="w-full px-6 py-3 bg-slate-100 text-slate-800 font-bold rounded-lg hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600 transition-colors"
                >
                    Continue with the free, ad-supported version
                </button>
            </div>
        </div>
      </div>
    </>
  );
};

export default Paywall;